import type { Money, OrderStatus, PaymentMethodKind } from '@sshop/domain';
import { money } from '@sshop/domain';

import type {
  AccountOrderDetailDto,
  AccountOrderItemDto,
  AccountOrderSummaryDto,
} from './account.dto';

export interface AccountOrderSummary {
  readonly id: string;
  readonly number: string;
  readonly status: OrderStatus;
  readonly placedAt: string | null;
  readonly total: Money;
  readonly itemCount: number;
  readonly paymentKind: PaymentMethodKind;
}

export interface AccountOrderPage {
  readonly orders: readonly AccountOrderSummary[];
  readonly page: number;
  readonly lastPage: number;
  readonly perPage: number;
  readonly total: number;
}

export interface AccountOrderItem {
  readonly productId: string;
  readonly name: string;
  readonly quantity: number;
  readonly unitPrice: Money;
  readonly lineTotal: Money;
}

export interface AccountOrderDetail extends AccountOrderSummary {
  readonly firstName: string;
  readonly lastName: string;
  readonly phone: string;
  readonly email: string;
  readonly city: string;
  readonly address: string;
  readonly notes: string | null;
  readonly items: readonly AccountOrderItem[];
}

export function toAccountOrderSummary(dto: AccountOrderSummaryDto): AccountOrderSummary {
  return {
    id: dto.id,
    number: dto.number,
    status: dto.status,
    placedAt: dto.placed_at,
    total: money(dto.total.amount, dto.total.currency),
    itemCount: dto.item_count,
    paymentKind: dto.payment_kind,
  };
}

function toItem(dto: AccountOrderItemDto): AccountOrderItem {
  return {
    productId: dto.product_id,
    name: dto.name,
    quantity: dto.quantity,
    unitPrice: money(dto.unit_price.amount, dto.unit_price.currency),
    lineTotal: money(dto.line_total.amount, dto.line_total.currency),
  };
}

export function toAccountOrderDetail(dto: AccountOrderDetailDto): AccountOrderDetail {
  return {
    ...toAccountOrderSummary(dto),
    firstName: dto.first_name,
    lastName: dto.last_name,
    phone: dto.phone,
    email: dto.email,
    city: dto.city,
    address: dto.address,
    notes: dto.notes,
    items: dto.items.map(toItem),
  };
}
